import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toastError, toastSuccess } from "../../utils/utils";
import { useGetOrganizations } from "../HandlingComponents/";

// GET /doctors
export const useGetDoctors = () => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const organizationsQuery = useGetOrganizations();

  return useQuery({
    queryKey: ["doctors"],
    queryFn: async () => {
      const res = await axiosInstance.get(`/doctors`, {
        headers: { Authorization: authHeader() },
      });

      if (authUser()?.role !== 1) return res.data;

      let data = res.data;
      let doctors = [];

      for (let d of data.doctors) {
        let org = organizationsQuery.data.organizations.find(
          (org) => org.id === d.org_id
        );

        // add org_name to doctor object
        doctors.push({
          ...d,
          org_name: org.name,
        });
      }

      data["doctors"] = doctors;
      return data;
    },
    enabled: organizationsQuery.isSuccess,
  });
};

// GET /doctors/:doctor_id
export const useGetDoctorById = (doctor_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["doctors", doctor_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/doctors/${doctor_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: doctor_id !== undefined && doctor_id !== null,
  });
};

// === PUT /doctors/:doctor_id ===
export const usePutDoctorById = (doctor_id, closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();
  const getOriginalData = useGetDoctorById(doctor_id);

  return useMutation({
    mutationFn: async (editedDoctor) => {
      if (editedDoctor === undefined) return;
      if (editedDoctor.profile !== undefined && editedDoctor.profile !== null)
        delete editedDoctor.profile; // TEMPORARY?
      const res = await axiosInstance.put(
        `/doctors/${doctor_id}`,
        editedDoctor,
        {
          headers: { Authorization: authHeader() },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["doctors", doctor_id]); // update views inside ViewDoctor
      queryClient.invalidateQueries(["doctors"]); // update views inside Doctors page
      toast.success("Dettagli professionista aggiornati!", toastSuccess);
    },
    onError: (data, variables, context) => {
      queryClient.setQueryData(["doctors", doctor_id], (oldData) => {
        return {
          ...oldData,
          ...getOriginalData.data,
        };
      });
      toast.error("Dettagli professionista non aggiornati", toastError);
    },
  });
};

// === POST /doctors ===
export const useNewDoctor = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newDoctor) => {
      const newUser = {
        username: newDoctor.email,
        email: newDoctor.email,
        mobile: newDoctor.mobile,
        first_name: newDoctor.first_name,
        last_name: newDoctor.last_name,
        user_type: 2,
        org_id: newDoctor.org_id,
      };

      // create doctor
      const resDoctor = await axiosInstance.post(`/doctors`, newDoctor, {
        headers: {
          Authorization: authHeader(),
        },
      });

      const doctor_id = resDoctor.data.id;
      newUser.doctor_id = doctor_id;

      // create user
      const resUser = await axiosInstance.post(`/users`, newUser, {
        headers: {
          Authorization: authHeader(),
        },
      });
    },
    onSuccess: (data) => {
      // close modal and display toast
      toast.success("Professionista aggiunto con successo!", toastSuccess);
      closeModal();

      queryClient.invalidateQueries(["doctors"]);
      queryClient.invalidateQueries(["users"]);
    },
    onError: (data) => {
      // close modal and display error
      toast.error("Errore nell'aggiunta del professionista", toastError);
      closeModal();
    },
  });
};

// === DELETE /doctors ===
export const useDeleteDoctor = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (doctor_id) => {
      const res = await axiosInstance.delete(`/doctors/${doctor_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: () => {
      // close modal and display toast
      toast.success("Professionista rimosso con successo!", toastSuccess);
      queryClient.invalidateQueries(["doctors"]);
    },
    onError: () => {
      // close modal and display error
      toast.error("Errore nella rimozione del professionista", toastError);
    },
  });
};
